import { useEffect, useRef, useState } from 'react';
import { OwnUserResponse, StreamChat } from 'stream-chat';
import { Chat, enTranslations, Streami18n } from 'stream-chat-react';

import { ChannelContainer } from './components/ChannelContainer/ChannelContainer';
import { Sidebar } from './components/Sidebar/Sidebar';

import { WorkspaceController } from './context/WorkspaceController';
import UserContext, { ConnectState } from './context/UserContext';

import type { StreamChatType } from './types';
import { Auth } from './components/Auth/Auth';
import { connectTiming } from './connectTiming';
import { installResilientQueryChannels } from './resilientQueryChannels';
import { isRejectedTokenError } from './streamAuth';

const apiKey = process.env.REACT_APP_STREAM_KEY;
const theme = 'light';

const i18nInstance = new Streami18n({
    language: 'en',
    translationsForLanguage: {
        ...enTranslations,
    },
});

const client = StreamChat.getInstance<StreamChatType>(apiKey!, { enableInsights: true, enableWSFallback: true, timeout: 10000 });
installResilientQueryChannels(client);

const App = () => {
    const [user, setUser] = useState(localStorage.getItem('user') || null);
    const [userToken, setUserToken] = useState(localStorage.getItem('userToken') || null);
    const [connectionId, setConnectionId] = useState<string | null>(null);
    const [app, setApp] = useState<{ key: string, name: string, knack_id?: string } | null>(localStorage.getItem('app') ? JSON.parse(localStorage.getItem('app')!) : null);
    const [moderatorMode, setModeratorMode] = useState(false)
    const [streamTeam, setStreamTeam] = useState('')
    const [userRole, setUserRole] = useState('')
    const [userIsModerator, setUserIsModerator] = useState(false)
    const [connectAttempt, setConnectAttempt] = useState(0)
    const [connectState, setConnectState] = useState<ConnectState>('idle')
    const [failedAttempt, setFailedAttempt] = useState<number | null>(null)
    const attemptRef = useRef(0)
    const pendingDisconnect = useRef<Promise<void>>(Promise.resolve())
    const pendingConnect = useRef<Promise<unknown>>(Promise.resolve())

    const signIn = (session: { user: string; userToken: string; app: { name: string; key: string; knack_id?: string } }) => {
        const attempt = ++attemptRef.current
        // Before React commits: a stale connect can still observe cancelled === false.
        connectTiming.afterAttemptAdvance?.()
        setUser(session.user)
        setUserToken(session.userToken)
        setApp(session.app)
        setConnectAttempt(attempt)
        return attempt
    }

    useEffect(() => {
        if (user) {
            localStorage.setItem('user', user);
        } else {
            localStorage.removeItem('user');
        }
        if (userToken) {
            localStorage.setItem('userToken', userToken);
        } else {
            localStorage.removeItem('userToken');
        }
        if (app) {
            localStorage.setItem('app', JSON.stringify(app));
        } else {
            localStorage.removeItem('app');
        }
    }, [user, userToken, connectionId, app]);

    useEffect(() => {
        let cancelled = false
        const myAttempt = attemptRef.current
        let timer: ReturnType<typeof setTimeout> | undefined
        const stale = () => cancelled || myAttempt !== attemptRef.current
        const clearTimer = () => {
            if (timer !== undefined) {
                clearTimeout(timer)
            }
        }

        const connectUser = async () => {
            if (!user || !userToken) return
            try {
                // Covers the waits below: a hung prior connect must not block this attempt's deadline.
                timer = setTimeout(() => {
                    if (stale()) return
                    setConnectState('failed')
                    setFailedAttempt(myAttempt)
                }, connectTiming.timeoutMs)
                // A rejected connectUser() calls disconnectUser(), which clears the token on a setTimeout.
                // Settle that attempt, then the disconnect, then one more turn, before connecting again.
                await pendingConnect.current.then(() => undefined, () => undefined)
                await pendingDisconnect.current
                await new Promise((resolve) => setTimeout(resolve, 0))
                if (stale()) return

                let connectPromise: Promise<unknown>
                try {
                    connectPromise = Promise.resolve(client.connectUser({ id: user }, userToken))
                } catch (error) {
                    connectPromise = Promise.reject(error)
                }
                pendingConnect.current = connectPromise.then(() => undefined, () => undefined)

                const connection = (await connectPromise) as { connection_id?: string; me?: OwnUserResponse & { _moderator?: boolean } } | void
                clearTimer()
                if (stale()) return
                if (connection) {
                    const nextConnectionId = connection.connection_id || '';
                    setConnectionId(nextConnectionId);
                    const userInfo = connection.me as OwnUserResponse & { _moderator?: boolean }
                    const _moderator = userInfo?._moderator
                    const teams: string[] | undefined = connection.me?.teams
                    const currentUserRole: string | undefined = connection.me?.role

                    if (teams && teams.length > 0) {
                        setStreamTeam(teams[0])
                    }

                    if (currentUserRole) {
                        setUserRole(currentUserRole)
                    }

                    if (_moderator) {
                        setUserIsModerator(_moderator)
                    }

                    if (nextConnectionId) {
                        setConnectState('idle')
                    } else {
                        setConnectState('failed')
                        setFailedAttempt(myAttempt)
                    }
                } else {
                    setConnectState('failed')
                    setFailedAttempt(myAttempt)
                }
            } catch (e: any) {
                clearTimer()
                if (stale()) return
                console.log(e.message)
                if (isRejectedTokenError(e)) {
                    setUser(null)
                    setUserToken(null)
                    setApp(null)
                }
                setConnectState('failed')
                setFailedAttempt(myAttempt)
            }
        }

        if (user && userToken) {
            setConnectState('connecting')
            connectUser();
            return () => {
                cancelled = true
                clearTimer()
                setConnectionId(null)
                pendingDisconnect.current = client.disconnectUser()
            }
        }
    }, [user, userToken, connectAttempt]);

    useEffect(() => {
        const handleColorChange = (color: string) => {
            const root = document.documentElement;
            if (color.length && color.length === 7) {
                root.style.setProperty('--primary-color', `${color}E6`);
                root.style.setProperty('--primary-color-alpha', `${color}1A`);
            }
        };

        window.addEventListener('message', (event) => handleColorChange(event.data));
        return () => {
            client.disconnectUser();
            window.removeEventListener('message', (event) => handleColorChange(event.data));
        };
    }, []);

    return (
        <UserContext.Provider value={{ app, setApp, user, setUser, userToken, setUserToken, moderatorMode, setModeratorMode, streamTeam, userRole, setUserRole, userIsModerator, signIn, connectState, failedAttempt }}>
            <div className='app__wrapper str-chat'>
                {(!user || !userToken || !connectionId) && (
                    <Auth />
                )}
                {user && userToken && connectionId && (
                    <Chat {...{ client, i18nInstance }} theme={`team ${theme}`}>
                        <WorkspaceController>
                            <Sidebar />
                            <ChannelContainer />
                        </WorkspaceController>
                    </Chat>
                )}
            </div>
        </UserContext.Provider>
    );
};

export default App;
